import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Printer, Pencil, Loader2, Wrench, Link2, CheckCircle2, FileCheck2,
} from "lucide-react";
import api, { money, fmtDate } from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge, { STATUSES } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get(`/invoices/${id}`).then(({ data }) => setInv(data)).catch(() => toast.error("Not found")).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  const changeStatus = async (status) => {
    try {
      await api.patch(`/invoices/${id}/status`, { status });
      setInv((x) => ({ ...x, status }));
      toast.success("Status updated");
    } catch { toast.error("Failed to update status"); }
  };

  const shareLink = async () => {
    try {
      const { data } = await api.post(`/invoices/${id}/share-link`);
      const url = data.path
        ? `${process.env.REACT_APP_API_BASE}${data.path}`
        : (data.url || data.link || "");
      if (!url) { toast.error("Could not create link"); return; }
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        try { copied = document.execCommand("copy"); } catch { copied = false; }
        document.body.removeChild(ta);
      }
      if (copied) toast.success("Link copied! You can now paste this into a text or email for your client.");
      else toast.message("Here is the shareable link", { description: url });
    } catch { toast.error("Failed to create link"); }
  };

  const convert = async () => {
    try {
      const { data } = await api.post(`/invoices/${id}/convert`);
      toast.success("Converted to invoice");
      if (data?.id) navigate(`/invoices/${data.id}`); else load();
    } catch { toast.error("Failed to convert"); }
  };

  if (loading) return <Layout title="Loading…"><div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div></Layout>;
  if (!inv) return <Layout title="Not found"><p className="text-slate-400">Document not found.</p></Layout>;

  const vehicle = [inv.vehicle_year, inv.vehicle_make, inv.vehicle_model].filter(Boolean).join(" ");
  const parts = (inv.items || []).filter((i) => i.kind !== "labor");
  const labor = (inv.items || []).filter((i) => i.kind === "labor");

  return (
    <Layout
      title={`${inv.doc_type === "estimate" ? "Estimate" : "Invoice"} ${inv.number || ""}`}
      action={
        <div className="flex flex-wrap gap-2 no-print">
          <Button variant="outline" data-testid="back-button" onClick={() => navigate("/invoices")}
            className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back</Button>
          {inv.doc_type === "estimate" && (
            <Button variant="outline" data-testid="convert-estimate-button" onClick={convert}
              className="bg-slate-900 border-slate-700 text-blue-400 hover:bg-slate-800"><FileCheck2 className="h-4 w-4 mr-1.5" /> Convert</Button>
          )}
          <Button variant="outline" data-testid="share-link-button" onClick={shareLink}
            className="bg-slate-900 border-slate-700 text-emerald-400 hover:bg-slate-800"><Link2 className="h-4 w-4 mr-1.5" /> Share Link</Button>
          <Button variant="outline" data-testid="edit-invoice-button" onClick={() => navigate(`/invoices/${id}/edit`)}
            className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800"><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>
          <Button data-testid="print-invoice-button" onClick={() => window.print()}
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"><Printer className="h-4 w-4 mr-1.5" /> Print / PDF</Button>
        </div>
      }
    >
      {/* Status bar */}
      <div className="flex flex-wrap items-center gap-3 mb-5 no-print animate-fade-up">
        <StatusBadge status={inv.status} />
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-slate-500">Change status</span>
          <Select value={inv.status} onValueChange={changeStatus}>
            <SelectTrigger data-testid="detail-status-select" className="h-9 w-40 bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 text-slate-200">
              {STATUSES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
            </SelectContent>
          </Select>
          {inv.status !== "paid" && (
            <Button size="sm" data-testid="mark-paid-button" onClick={() => changeStatus("paid")}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark Paid</Button>
          )}
        </div>
      </div>

      {/* Printable sheet */}
      <div className="print-area rounded-xl border border-slate-800 bg-slate-900 p-6 md:p-10 max-w-4xl animate-fade-up">
        <div className="flex flex-col sm:flex-row justify-between gap-6 pb-6 border-b border-slate-800">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-9 w-9 rounded-lg bg-amber-500 flex items-center justify-center">
                <Wrench className="h-5 w-5 text-slate-950" />
              </div>
              <span className="font-head font-bold text-lg text-slate-50 print-accent">{user?.business_name || user?.name || "My Shop"}</span>
            </div>
            <div className="text-sm text-slate-400 space-y-0.5">
              {user?.address && <p>{user.address}</p>}
              {user?.phone && <p>{user.phone}</p>}
              {user?.email && <p>{user.email}</p>}
              {user?.license && <p>Lic: {user.license}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className="font-head font-black text-2xl uppercase text-slate-100 print-accent">{inv.doc_type === "estimate" ? "Estimate" : "Invoice"}</p>
            <p className="font-mono text-sm text-slate-400 mt-1">{inv.number}</p>
            <p className="text-sm text-slate-400 mt-2">{fmtDate(inv.created_at)}</p>
          </div>
        </div>

        {/* Bill to + vehicle */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-6">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Bill To</p>
            <p className="font-semibold text-slate-100">{inv.customer_name}</p>
            {inv.customer_business_name && <p className="text-sm text-slate-400">{inv.customer_business_name}</p>}
            {inv.customer_business_address && <p className="text-sm text-slate-400">{inv.customer_business_address}</p>}
            {inv.customer_phone && <p className="text-sm text-slate-400">{inv.customer_phone}</p>}
            {inv.customer_email && <p className="text-sm text-slate-400">{inv.customer_email}</p>}
          </div>
          <div className="sm:text-right">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Vehicle</p>
            <p className="font-semibold text-slate-100">{vehicle || "—"}</p>
            {inv.vehicle_vin && <p className="text-sm text-slate-400">VIN: {inv.vehicle_vin}</p>}
          </div>
        </div>

        {/* Items */}
        <table className="w-full text-sm border-t border-slate-800">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-500">
              <th className="text-left font-semibold py-2.5">Description</th>
              <th className="text-left font-semibold py-2.5 w-20">Type</th>
              <th className="text-right font-semibold py-2.5 w-16">Qty</th>
              <th className="text-right font-semibold py-2.5 w-28">Unit</th>
              <th className="text-right font-semibold py-2.5 w-28">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/70">
            {[...parts, ...labor].map((it, i) => (
              <tr key={i}>
                <td className="py-3 text-slate-200">{it.description}</td>
                <td className="py-3 text-slate-500 capitalize">{it.kind}</td>
                <td className="py-3 text-right text-slate-300 tabular-nums">{it.quantity}</td>
                <td className="py-3 text-right text-slate-300 tabular-nums">{money(it.unit_price)}</td>
                <td className="py-3 text-right text-slate-100 font-medium tabular-nums">{money(Number(it.quantity) * Number(it.unit_price))}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end pt-5">
          <div className="w-full sm:w-64 space-y-2 text-sm">
            <div className="flex justify-between text-slate-400"><span>Subtotal</span><span className="tabular-nums text-slate-200">{money(inv.subtotal)}</span></div>
            <div className="flex justify-between text-slate-400"><span>Tax ({inv.tax_rate || 0}%)</span><span className="tabular-nums text-slate-200">{money(inv.tax)}</span></div>
            <div className="flex justify-between items-center pt-2 border-t border-slate-800">
              <span className="font-head font-bold text-slate-100">Total</span>
              <span className="font-head font-black text-xl text-amber-400 print-accent tabular-nums">{money(inv.total)}</span>
            </div>
          </div>
        </div>

        {(() => {
          const pays = [
            { label: "Venmo", value: user?.pay_venmo },
            { label: "Cash App", value: user?.pay_cashapp },
            { label: "PayPal", value: user?.pay_paypal },
            { label: "Zelle", value: user?.pay_zelle },
            { label: "Chime", value: user?.pay_chime },
          ].filter((p) => p.value);
          if (!pays.length) return null;
          return (
            <div className="mt-6 pt-4 border-t border-slate-800" data-testid="payment-options">
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Payment Options</p>
              <div className="flex flex-wrap gap-2">
                {pays.map((p) => (
                  <span key={p.label} className="text-sm px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200">
                    <span className="text-slate-500">{p.label}:</span> <span className="print-accent text-amber-400 font-medium">{p.value}</span>
                  </span>
                ))}
              </div>
            </div>
          );
        })()}

        {inv.notes && (
          <div className="mt-6 pt-4 border-t border-slate-800">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Notes</p>
            <p className="text-sm text-slate-400 whitespace-pre-wrap">{inv.notes}</p>
          </div>
        )}
        {inv.terms && (
          <div className="mt-4 pt-4 border-t border-slate-800">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Terms</p>
            <p className="text-[11px] leading-relaxed text-slate-500 whitespace-pre-wrap">{inv.terms}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
